import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { formService } from "../../../services/api/formService";
import Loading from "./Loading";
import DragDrop from "./DragDrop";
import CustomSelect from "./CustomSelect";
import CustomInput from "./CustomInput";
import { NhomSelectData } from "../../../utils/NhomSelectData";
import { useNavigate, useParams } from "react-router-dom";
import {
  buildDocumentManagerState,
  buildIntellectualPropertyCatalogState,
  isDocumentManagerContext,
  isIntellectualPropertyCatalogContext,
  withDocumentManagerSearch,
  withIntellectualPropertyCatalogSearch,
} from "../../../utils/navigationContext";

const OriginalProfileCheckbox = ({ checked, onChange }) => (
  <label className="flex items-center gap-3 cursor-pointer group pointer-events-auto">
    <div className="relative flex items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="peer appearance-none size-5 border-2 border-blue-200 rounded-md checked:bg-blue-500 checked:border-blue-500 transition-all duration-200"
      />
      <svg
        className="absolute size-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <span className="font-sf-medium text-gray-700 group-hover:text-blue-600 transition-colors">
      Hồ sơ gốc tài sản trí tuệ
    </span>
  </label>
);

const DynamicForm = ({
  formData,
  initData,
  data,
  id,
  token,
  isEdit = false,
  category,
  representData = {
    companyName: "Công ty TNHH Masterbrand",
    address:
      "Số 104/13 Nguyễn Thái Sơn, Phường 3, Quận Gò Vấp, Tp. Hồ Chí Minh",
  },
  branData,
  serviceId,
  serviceName,
  navigationState,
  navigationSearch = "",
}) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const navigationLocationContext = {
    search: navigationSearch,
    state: navigationState,
  };
  const isFromIntellectualPropertyCatalog =
    isIntellectualPropertyCatalogContext(navigationLocationContext);
  const isFromDocumentManager =
    isDocumentManagerContext(navigationLocationContext);
  const intellectualPropertyCatalogState = buildIntellectualPropertyCatalogState(
    navigationState || {},
  );
  const documentManagerState = buildDocumentManagerState(navigationState || {});
  const contextState = isFromIntellectualPropertyCatalog
    ? intellectualPropertyCatalogState
    : isFromDocumentManager
      ? documentManagerState
      : navigationState;
  const withContextSearch = (path) => {
    if (isFromIntellectualPropertyCatalog) {
      return withIntellectualPropertyCatalogSearch(path);
    }

    if (isFromDocumentManager) {
      return withDocumentManagerSearch(path);
    }

    return path;
  };
  const [formState, setFormState] = useState({});
  const [files, setFiles] = useState({});
  const [sectionData, setSectionData] = useState({});
  const [loading, setLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [represent, setRepresent] = useState(representData);
  const [brand, setBrand] = useState(branData || "");
  const [isLookup, setIsLookup] = useState(true);
  const [isRequire, setIsRequire] = useState(false);
  const [isOriginal, setIsOriginal] = useState(false);
  const [contentRequire, setContentRequire] = useState("");

  const handleRepresentChange = (field, value) => {
    setRepresent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (data) {
      setImageSrc(data.profile_image);
      setIsLookup(data.isLookup ?? true);
      setIsRequire(data.isRequire ?? false);
      setIsOriginal(data.isOriginal ?? false);
      setContentRequire(data.contentRequire ?? "");
    }
    const initialSectionData = {};

    if (formData) {
      formData.profiles?.forEach((profile, profileIdx) => {
        profile.data?.forEach((section, secIdx) => {
          const sectionKey = `${profileIdx}-${secIdx}`;
          initialSectionData[sectionKey] = [
            {
              name: section.type,
              id: Date.now(),
              fields: section.fields.flat(),
            },
          ];
        });
      });
      setSectionData(initialSectionData);
    }

    // Đồng bộ số lượng fields giữa initData và formData
    if (initData) {
      const initialFormState = {};
      initData.profiles?.forEach((profile, profileIdx) => {
        profile.data?.forEach((section, secIdx) => {
          const sectionKey = `${profileIdx}-${secIdx}`;

          // Nếu số lượng fields trong initData nhiều hơn formData, thêm các fields bị thiếu
          if (
            initialSectionData[sectionKey] &&
            initialSectionData[sectionKey][0].fields.length <
              section.fields.flat().length
          ) {
            const missingFields = section.fields
              .flat()
              .slice(initialSectionData[sectionKey][0].fields.length);
            // Thêm các fields còn thiếu vào sectionData
            setSectionData((prev) => ({
              ...prev,
              [sectionKey]: [
                {
                  ...prev[sectionKey][0],
                  fields: [...prev[sectionKey][0].fields, ...missingFields],
                },
              ],
            }));
          }

          // Thiết lập giá trị ban đầu cho các fields
          section.fields?.flat().forEach((field, fieldIdx) => {
            const key = `${sectionKey}-${Date.now()}-${fieldIdx}`;
            initialFormState[key] = field.value || "";
          });
        });
      });

      setFormState((prev) => ({ ...prev, ...initialFormState }));
    }
  }, [formData, initData, data]);

  const handleAddField = (profileIndex, sectionIndex) => {
    const sectionKey = `${profileIndex}-${sectionIndex}`;
    const section = formData.profiles[profileIndex].data[sectionIndex];

    const newFields = section.fields[0].map((field) => ({
      ...field,
      value: "",
    }));
    setSectionData((prevState) => {
      const updatedFields = [...prevState[sectionKey][0].fields, ...newFields];

      return {
        ...prevState,
        [sectionKey]: [
          {
            ...prevState[sectionKey][0],
            fields: updatedFields,
          },
        ],
      };
    });
  };

  const handleRemoveFieldGroup = (
    sectionKey,
    sectionInstanceId,
    groupStartIndex,
    groupSize,
  ) => {
    setSectionData((prevState) => {
      const sectionInstance = prevState[sectionKey]?.[0];
      if (!sectionInstance) return prevState;

      return {
        ...prevState,
        [sectionKey]: [
          {
            ...sectionInstance,
            fields: sectionInstance.fields.filter(
              (_, fieldIdx) =>
                fieldIdx < groupStartIndex ||
                fieldIdx >= groupStartIndex + groupSize,
            ),
          },
        ],
      };
    });

    const removeAndReindexValues = (previousState) => {
      const nextState = { ...previousState };
      const keyPrefix = `${sectionKey}-${sectionInstanceId}-`;
      const indexedValues = Object.entries(previousState)
        .filter(([key]) => key.startsWith(keyPrefix))
        .map(([key, value]) => ({
          key,
          value,
          index: Number(key.slice(keyPrefix.length)),
        }));

      indexedValues.forEach(({ key }) => delete nextState[key]);
      indexedValues.forEach(({ index, value }) => {
        const isRemoved =
          index >= groupStartIndex &&
          index < groupStartIndex + groupSize;
        if (isRemoved) return;

        const nextIndex =
          index >= groupStartIndex + groupSize ? index - groupSize : index;
        nextState[`${keyPrefix}${nextIndex}`] = value;
      });

      return nextState;
    };

    setFormState(removeAndReindexValues);
    setFiles(removeAndReindexValues);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Chỉ chấp nhận định dạng JPEG/PNG.");
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error("Kích thước tệp vượt quá 25MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageSrc(reader.result || "/images/avatar-default.png");
    };
    reader.onerror = () => {
      toast.error("Không thể đọc tệp. Vui lòng thử lại.");
    };
    reader.readAsDataURL(file);

    setImageFile(file);
  };

  const handleChange = (fieldName, value, sectionKey, uniqueId, fieldIdx) => {
    setFormState((prev) => ({
      ...prev,
      [`${sectionKey}-${uniqueId}-${fieldIdx}`]: value,
    }));
  };

  const handleFileChange = (
    fieldName,
    file,
    sectionKey,
    uniqueId,
    fieldIdx,
  ) => {
    const key = `${sectionKey}-${uniqueId}-${fieldIdx}`;

    setFiles((prev) => {
      const updatedFiles = { ...prev };

      if (file) {
        updatedFiles[key] = file; // Cập nhật file mới
      } else {
        delete updatedFiles[key]; // Xóa key khỏi object nếu file là null
      }

      return updatedFiles;
    });
  };

  const transformFormData = () => {
    const transformed = [];
    Object.keys(sectionData).forEach((sectionKey) => {
      sectionData[sectionKey].forEach((sectionInstance) => {
        const sectionName = sectionInstance.name;
        const fields = sectionInstance.fields.map((field, fieldIdx) => {
          const key = `${sectionKey}-${sectionInstance.id}-${fieldIdx}`;

          let value = formState[key] || "";
          if (field.type === "select" && value && value.label) {
            value = value.label;
          }

          return {
            name: field.name,
            value,
            fieldType: field.type,
          };
        });
        transformed.push({
          type: sectionName || "unknown",
          fields,
        });
      });
    });
    return transformed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = [];
    if (brand && !brand.trim()) {
      errors.push("Nhãn hiệu là bắt buộc.");
    }

    if (represent.companyName && !represent.companyName.trim()) {
      errors.push("Tên tổ chức đại diện là bắt buộc.");
    }
    if (represent.address && !represent.address.trim()) {
      errors.push("Địa chỉ tổ chức đại diện là bắt buộc.");
    }

    Object.keys(sectionData).forEach((sectionKey) => {
      sectionData[sectionKey].forEach((sectionInstance) => {
        sectionInstance.fields.forEach((field, fieldIdx) => {
          const key = `${sectionKey}-${sectionInstance.id}-${fieldIdx}`;
          const value = formState[key] || "";

          if (field.required && !value) {
            errors.push(`${field.name} là trường bắt buộc.`);
          }
        });
      });
    });

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    const transformedData = transformFormData();
    const formDataPayload = new FormData();
    formDataPayload.append("info", JSON.stringify(transformedData));
    formDataPayload.append("represent", JSON.stringify(represent));
    // Append the image file
    if (imageFile && isEdit) {
      formDataPayload.append("image", imageFile);
    }
    formDataPayload.append("brand", brand);
    formDataPayload.append("isLookup", isLookup);
    formDataPayload.append("isRequire", isRequire);
    formDataPayload.append("isOriginal", isOriginal);
    formDataPayload.append("contentRequire", contentRequire);

    // Xác định key cuối cùng trong sectionData
    const sectionKeys = Object.keys(sectionData);
    const lastSectionKey = sectionKeys[sectionKeys.length - 1];

    let galleryArray = [];

    sectionData[lastSectionKey]?.forEach((sectionInstance) => {
      sectionInstance.fields.forEach((field, fieldIdx) => {
        const key = `${lastSectionKey}-${sectionInstance.id}-${fieldIdx}`;

        const currentValue = formState[key];
        const newFiles = files[key];

        if (!currentValue && !newFiles) {
          galleryArray.push("null");
          return;
        }

        if (!currentValue && newFiles) {
          galleryArray.push(newFiles);
          return;
        }

        if (currentValue && !newFiles) {
          galleryArray.push(currentValue);
          return;
        }

        if (currentValue && newFiles) {
          const merged = Array.isArray(currentValue)
            ? [...currentValue, ...newFiles]
            : [currentValue, ...newFiles];
          galleryArray.push(merged);
          return;
        }
      });
    });

    var galleryOrder = [];

    galleryArray.forEach((item) => {
      if (item === null || item === "null") {
        galleryOrder.push("null");
      } else if (Array.isArray(item)) {
        let url_count = 0;
        let fileCount = 0;

        item.forEach((file) => {
          formDataPayload.append("gallery", file);
          if (typeof file === "string") {
            url_count++;
          } else {
            fileCount++;
          }
        });

        galleryOrder.push(url_count + "-" + fileCount);
      } else {
        formDataPayload.append("gallery", item);
        if (typeof item === "string") {
          galleryOrder.push("1-0");
        } else {
          galleryOrder.push("0-1");
        }
      }
    });

    formDataPayload.append("galleryOrder", JSON.stringify(galleryOrder));

    // for (let pair of formDataPayload.entries()) {
    //   console.log(pair[0], pair[1]);
    // }
    try {
      setLoading(true);

      if (!isEdit)
        await formService.add_new(id, slug, formDataPayload, serviceId, token);
      else {
        await formService.update(id, formDataPayload, token);
      }
      toast.success("Đăng ký dịch vụ và tạo hồ sơ thành công!");
    } catch (error) {
      toast.dismiss();
      // Handle errors
      toast.error(
        error.response
          ? `${error.response?.data?.message}`
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Submit Error:", error);
    } finally {
      setLoading(false);
      if (!isEdit) navigate("/quan-ly-ho-so");
      else {
        const detailPath = `/quan-ly-dich-vu-khach-hang/ho-so/${id}`;

        navigate(withContextSearch(detailPath), {
          state: contextState,
        });
      }
    }
  };

  const renderDragDropField = (
    field,
    fieldKey,
    currentValue,
    onRemoveItem,
    onAppendFiles,
  ) => {
    if (Array.isArray(currentValue)) {
      return (
        <div className="space-y-3">
          {currentValue.length > 0 && (
            <ul className="space-y-2">
              {currentValue.map((url, index) => (
                <li
                  key={index}
                  className="hover:border-blue-300 border-blue-600 border-2 flex items-center justify-between w-full bg-gray-100 p-2 py-4 rounded-md"
                >
                  <div className="flex gap-2 items-center">
                    <img
                      src="/images/document.png"
                      alt="Icon"
                      className="size-[20px]"
                    />
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-blue-600 hover:underline break-all text-sm"
                    >
                      {(() => {
                        const fileName = url?.split("/").pop();

                        return fileName?.length > 50
                          ? `${fileName.substring(0, 47)}...`
                          : fileName;
                      })()}
                    </a>
                  </div>

                  <button type="button" onClick={() => onRemoveItem(index)}>
                    <img
                      src="/icons/x-close.svg"
                      alt="Remove"
                      className="h-4 w-4"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <DragDrop
            label="Tải thêm tài liệu"
            onFileChange={(newFiles) => {
              if (newFiles?.length > 0) {
                onAppendFiles(newFiles);
              }
            }}
            maxFiles={field.maxValue - currentValue.length}
          />
        </div>
      );
    }

    if (typeof currentValue === "string" && currentValue.trim() !== "") {
      return (
        <div className="border-2 w-full border-solid rounded-md p-4 text-center cursor-pointer relative flex flex-col justify-center items-center space-y-4 hover:border-blue-300 border-blue-600">
          <div className="flex items-center justify-start w-full">
            <label
              htmlFor={field.name}
              className="block text-sm text-gray-500 cursor-pointer"
            >
              <div className="flex gap-2 items-center ">
                <img
                  src="/images/document.png"
                  alt="Icon"
                  className="size-[20px]"
                />
                <a
                  href={formState[`${fieldKey}`]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-blue-600 hover:underline break-all text-sm"
                >
                  {(() => {
                    // Extract the filename from the URL
                    const fileName = formState[`${fieldKey}`]?.split("/").pop();

                    // Check if the filename length exceeds 50 characters
                    return fileName?.length > 50
                      ? `${fileName.substring(0, 47)}...`
                      : fileName;
                  })()}
                </a>
              </div>
            </label>
            <button
              type="button"
              onClick={() =>
                setFormState((prev) => ({
                  ...prev,
                  [`${fieldKey}`]: "",
                }))
              }
              className="absolute right-2"
            >
              <img src="/icons/x-close.svg" alt="Remove" className="h-4 w-4" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <DragDrop
        label="Kéo thả file để tải lên"
        onFileChange={(files) => {
          setFiles((prev) => ({
            ...prev,
            [fieldKey]: files,
          }));
        }}
        maxFiles={field.maxValue}
      />
    );
  };

  let sectionCounter = 0;
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="bg-white p-10 rounded-lg shadow-lg h-full overflow-auto">
      <div className="flex justify-center items-center gap-6 mb-6">
        {isEdit && (
          <div className="flex items-center">
            <label
              htmlFor="image-upload"
              className="cursor-pointer w-28 h-28 bg-gray-100 flex items-center justify-center rounded-md overflow-hidden relative hover:opacity-60"
            >
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt="Uploaded"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="flex items-center justify-center text-gray-400">
                  <img src="/icons/camera-icon.svg" alt="Camera icon" />
                </div>
              )}

              <input
                id="image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          </div>
        )}
        <h1 className="text-3xl font-bold text-blue-600 uppercase">
          {serviceName}
        </h1>
      </div>

      <div className="space-y-4">
        {isEdit && (
          <div className="space-y-4">
            <h2 className="font-sf-semibold">Tổ chức đại diện SHCN:</h2>
            <div className={`grid grid-cols-1 gap-4`}>
              <CustomInput
                placeholder={"Tên tổ chức đại diện"}
                required
                value={represent.companyName}
                onChange={(value) =>
                  handleRepresentChange("companyName", value)
                }
              />
              <CustomInput
                placeholder={"Địa chỉ tổ chức đại diện"}
                required
                value={represent.address}
                onChange={(value) => handleRepresentChange("address", value)}
              />
            </div>
          </div>
        )}
        <div className="space-y-4">
          <h2 className="font-sf-semibold">
            {category === "Nhãn hiệu" ? "Nhãn hiệu:" : "Tên hồ sơ:"}
          </h2>
          <div className={`grid grid-cols-1 gap-4`}>
            <CustomInput
              placeholder={
                category === "Nhãn hiệu"
                  ? "Nhãn hiệu (nếu có)"
                  : "Tên hồ sơ (nếu có)"
              }
              // required
              value={brand}
              onChange={(value) => setBrand(value)}
            />
          </div>
        </div>
        {formData.profiles?.map((profile, profileIdx) => (
          <div
            key={profileIdx}
            className={`grid grid-cols-${profile.cols} gap-4`}
          >
            {profile.data?.map((section, secIdx) => {
              const sectionKey = `${profileIdx}-${secIdx}`;
              sectionCounter += 1;

              return (
                <div key={secIdx} className="space-y-4">
                  <h2 className="font-sf-semibold">
                    {sectionCounter}. {section.type}
                  </h2>
                  {sectionData[sectionKey]?.map((sectionInstance) => (
                    <div
                      key={sectionInstance.id}
                      className={`grid grid-cols-${section.cols} ${
                        section.type === "Danh mục hàng hóa/dịch vụ"
                          ? "gap-x-0 gap-y-3"
                          : "gap-4"
                      }`}
                    >
                      {sectionInstance.fields.map((field, fieldIdx) => {
                        const fieldKey = `${sectionKey}-${sectionInstance.id}-${fieldIdx}`;
                        const fieldGroupSize = section.fields[0]?.length || 1;
                        const isGoodsCategorySection =
                          section.type === "Danh mục hàng hóa/dịch vụ";
                        const isFieldGroupEnd =
                          (fieldIdx + 1) % fieldGroupSize === 0;
                        const isFile =
                          field.type === "pdf" || field.type === "image";
                        const isSelect = field.type === "select";

                        const FieldComponent = isFile
                          ? DragDrop
                          : isSelect
                            ? CustomSelect
                            : CustomInput;

                        return (
                          <div
                            key={fieldIdx}
                            className={`col-span-${field.span || 1} ${
                              isGoodsCategorySection
                                ? `bg-[#F8F9FB] py-3 ${
                                    isFieldGroupEnd
                                      ? "flex items-center gap-3 pl-3"
                                      : "rounded-l-2xl px-3"
                                  }`
                                : ""
                            }`}
                          >
                            {isFile ? (
                              <>
                                <h2 className="font-sf-semibold mb-2">
                                  + {field.name}
                                </h2>
                                {renderDragDropField(
                                  field,
                                  fieldKey,
                                  formState[fieldKey],
                                  (removeIndex) => {
                                    const updated = formState[fieldKey].filter(
                                      (_, i) => i !== removeIndex,
                                    );
                                    setFormState((prev) => ({
                                      ...prev,
                                      [fieldKey]: updated,
                                    }));
                                  },
                                  (newFiles) => {
                                    setFiles((prev) => ({
                                      ...prev,
                                      [fieldKey]: newFiles,
                                    }));
                                  },
                                )}
                              </>
                            ) : (
                              <>
                                <div className="min-w-0 flex-1">
                                  <FieldComponent
                                    key={fieldIdx}
                                    label={field.name}
                                    options={
                                      isSelect ? NhomSelectData : undefined
                                    }
                                    placeholder={field.name}
                                    type={
                                      field.type === "email" ? "email" : "text"
                                    }
                                    required={field.required || false}
                                    isTextarea={field.textarea || false}
                                    value={formState[fieldKey] || ""}
                                    onChange={(value) =>
                                      handleChange(
                                        field.name,
                                        value,
                                        sectionKey,
                                        sectionInstance.id,
                                        fieldIdx,
                                      )
                                    }
                                    onFileChange={(file) =>
                                      handleFileChange(
                                        field.name,
                                        file,
                                        sectionKey,
                                        sectionInstance.id,
                                        fieldIdx,
                                      )
                                    }
                                  />
                                </div>
                                {isGoodsCategorySection && isFieldGroupEnd && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveFieldGroup(
                                        sectionKey,
                                        sectionInstance.id,
                                        fieldIdx - fieldGroupSize + 1,
                                        fieldGroupSize,
                                      )
                                    }
                                    className="-my-3 flex w-12 self-stretch shrink-0 items-center justify-center rounded-r-2xl bg-[#FF5C64] text-white transition-colors hover:bg-[#F0444D]"
                                    title="Gỡ danh mục hàng hóa/dịch vụ"
                                    aria-label="Gỡ danh mục hàng hóa/dịch vụ"
                                  >
                                    <svg
                                      className="size-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {section.add && (
                    <div className="mt-4">
                      <button
                        className="text-blue-600 gap-1 hover:font-bold flex items-center"
                        onClick={() => handleAddField(profileIdx, secIdx)}
                      >
                        <img src="/icons/add-circle.svg" alt="Add icon" />
                        Thêm <span className="lowercase"> {section.type}</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        <div className="mt-8 space-y-4 bg-blue-50/50 p-6 rounded-xl border border-blue-100">
          <h4 className="font-sf-semibold text-[#0457C2] uppercase text-sm tracking-wide">
            Thông tin bổ sung
          </h4>

          <div className="flex flex-col gap-4">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={isLookup}
                  onChange={(e) => {
                    setIsLookup(e.target.checked);
                    if (e.target.checked) setIsRequire(false);
                  }}
                  className="peer appearance-none size-5 border-2 border-blue-200 rounded-md checked:bg-blue-500 checked:border-blue-500 transition-all duration-200"
                />
                <svg
                  className="absolute size-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <span className="font-sf-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                Có yêu cầu Tra cứu
              </span>
            </label>

            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={isRequire}
                    onChange={(e) => {
                      setIsRequire(e.target.checked);
                      if (e.target.checked) setIsLookup(false);
                    }}
                    className="peer appearance-none size-5 border-2 border-blue-200 rounded-md checked:bg-blue-500 checked:border-blue-500 transition-all duration-200"
                  />
                  <svg
                    className="absolute size-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <span className="font-sf-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                  Có yêu cầu khác
                </span>
              </label>

              <div className="ml-8 motion-safe:animate-[fadeIn_200ms_ease-out]">
                <textarea
                  value={contentRequire}
                  onChange={(e) => setContentRequire(e.target.value)}
                  disabled={isLookup}
                  placeholder="Nhập nội dung yêu cầu khác..."
                  className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none min-h-[100px] text-sm transition-all ${
                    isLookup
                      ? "bg-gray-50 border-gray-200 text-gray-400 cursor-default"
                      : "bg-white border-blue-100 text-gray-700 placeholder:text-gray-400"
                  }`}
                />
              </div>
            </div>
          </div>

          <OriginalProfileCheckbox
            checked={isOriginal}
            onChange={setIsOriginal}
          />
        </div>
      </div>

      <div className="flex justify-end mt-10 space-x-4">
        <button
          type="reset"
          onClick={() => {
            if (
              window.confirm(
                "Bạn có chắc chắn muốn hủy chỉnh sửa hồ sơ này không?",
              )
            ) {
              setFormState({});
              setFiles({});
              const detailPath = `/quan-ly-dich-vu-khach-hang/ho-so/${id}`;

              if (isEdit) {
                navigate(withContextSearch(detailPath), {
                  state: contextState,
                });
                return;
              }

              navigate("/quan-ly-ho-so");
            }
          }}
          className="py-2 px-6 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Hủy
        </button>
        <button
          onClick={handleSubmit}
          className="py-2 px-6 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Nộp hồ sơ
        </button>
      </div>
    </div>
  );
};

export default DynamicForm;
